import LogRocket from 'logrocket';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import './i18n';
import AsyncStorage from './AsyncStorageShim';
import ListsManager from './services/ListsManager';
import AdminManager from './services/AdminManager';
import API_BASE_URL, { IS_LOCAL } from './config';

// Import Firebase configuration
import { remoteConfig } from './firebase';
import { fetchAndActivate, getValue } from 'firebase/remote-config';

// Ensure React Native Web polyfills are loaded
import 'react-native-web';

LogRocket.init('tixfqz/reactfirst-ai');

// Initialize Remote Config and ListsManager baseUrl
(async () => {
  try {
    if (IS_LOCAL) {
      // Local dev: skip Remote Config entirely — always use localhost.
      // Remote Config would overwrite with the production URL.
      ListsManager.setBaseUrl(API_BASE_URL);
      AdminManager.setBaseUrl(API_BASE_URL);
      console.log('🔧 Local mode: using', API_BASE_URL);
    } else {
      // Production: use Firebase Remote Config (may override API_BASE_URL).
      remoteConfig.defaultConfig = {
        backend_url: API_BASE_URL
      };

      await fetchAndActivate(remoteConfig);

      const backendUrl = getValue(remoteConfig, 'backend_url').asString();

      if (backendUrl) {
        ListsManager.setBaseUrl(backendUrl);
        AdminManager.setBaseUrl(backendUrl);
      } else {
        console.warn('⚠️ No backend URL found in Remote Config, using default');
      }
    }
  } catch (error) {
    console.error('❌ Error initializing Remote Config:', error);
  }

  // Add startup debugging
  console.log('🚀 App starting with:');
  console.log('📡 Protocol:', window.location.protocol);
  console.log('🌐 Hostname:', window.location.hostname);
  console.log('📢 ListsManager baseUrl:', ListsManager.baseUrl);

  // Check if we're on the login page
  const isLoginPage = window.location.pathname === '/login';

  // Only initialize default token if we're not on the login page and no token exists
  // This prevents re-setting the token after logout
  if (!isLoginPage) {
    try {
      const existingToken = await AsyncStorage.getItem('userToken');
      if (!existingToken) {
        console.log('No token found, user should be redirected to login');
      } else {
        console.log('AsyncStorage already has a userToken');
      }
    } catch (error) {
      console.error('Error checking AsyncStorage:', error);
    }
  }

  // Render the app after initialization.
  //
  // The marketing routes are prerendered to static HTML at build time (see
  // scripts/prerender.js) so crawlers and link-preview bots get real content
  // and users get a fast first paint. We deliberately use createRoot (not
  // hydrateRoot): the prerender is always English, but a returning visitor's
  // client may render Hebrew/French (RTL/LTR) — hydration would mismatch.
  // createRoot().render() cleanly replaces the static shell instead.
  const container = document.getElementById('root');
  createRoot(container).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
})();